'use client';

import { AutoComplete } from '@/components/ui/autocomplete';
import { Label } from '@/components/ui/label';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '~/components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '~/components/ui/popover';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from '~/api/trpc';
import { Step, SelectedBookableItem, useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useShallow } from 'zustand/react/shallow';
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from '~/stores/user-store';
import { TREATMENT_COLORS } from '~/lib/constants';

type BookableItemSearchProps = {
    storeId?: number;
};

type SelectedAddOn = {
    addOnId: string;
    addOnName: string;
    variantId: string;
    variantName: string;
    priceCents: number;
};

export const BookableItemSearch = ({ storeId }: BookableItemSearchProps) => {
    const { timezone } = useUserStore(
        useShallow(state => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    );

    const {
        steps, setSteps,
        colorIndex, setColorIndex,
        selectedBookableItems, setSelectedBookableItems,
        addBookableItem, removeBookableItem,
        refreshSelectableResources,
    } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
            setSteps: state.setSteps,
            colorIndex: state.colorIndex,
            setColorIndex: state.setColorIndex,
            selectedBookableItems: state.selectedBookableItems,
            setSelectedBookableItems: state.setSelectedBookableItems,
            addBookableItem: state.addBookableItem,
            removeBookableItem: state.removeBookableItem,
            refreshSelectableResources: state.refreshSelectableResources,
        }))
    );

    const trpcUtils = trpc.useUtils();

    // Search state
    const [searchValue, setSearchValue] = useState('');
    const [selectedValue, setSelectedValue] = useState('');

    // Dialog state for variant/add-on selection
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
    const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);

    // Choice dialog state for bundle-linked items (Bundle / One-off / Redemption)
    type ChoiceBundle = { id: string; name: string; unitQuantity: number; unitPriceGross: number };
    const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
    const [choiceItem, setChoiceItem] = useState<Awaited<ReturnType<typeof trpcUtils.appointments.searchBookableItems.fetch>>[number] | null>(null);
    const [choiceVariantId, setChoiceVariantId] = useState<string | null>(null);
    const [choiceAddOns, setChoiceAddOns] = useState<SelectedAddOn[]>([]);
    const [choiceBundle, setChoiceBundle] = useState<ChoiceBundle | null>(null);

    // Post-selection add-on popover state
    const [addOnPopoverOpenFor, setAddOnPopoverOpenFor] = useState<string | null>(null);
    const [pendingAddOnSelections, setPendingAddOnSelections] = useState<string[]>([]);

    // Cache full bookable item data (including add-ons with treatment variant steps)
    // so it's available when adding add-ons post-selection
    const bookableItemDataCache = useRef<Map<string, Awaited<ReturnType<typeof trpcUtils.appointments.searchBookableItems.fetch>>[number]>>(new Map());

    // Fetch bookable items (only when a store is selected)
    const bookableItemsQuery = trpc.appointments.searchBookableItems.useQuery({
        storeId: storeId,
        search: searchValue,
    }, {
        enabled: !!storeId,
    });

    const [selectedItem, setSelectedItem] = useState<Awaited<ReturnType<typeof trpcUtils.appointments.searchBookableItems.fetch>>[number] | null>(null);

    const bookableItems = bookableItemsQuery.data ?? [];

    // Client-side store filtering (safety net that supplements server-side filtering)
    // Ensures variant options, add-ons, and bundles are properly scoped to the selected store
    const filteredBookableItems = useMemo(() => {
        if (!storeId) return [];

        return bookableItems
            .map(item => {
                if (item.type === 'SERVICE') {
                    // Filter variant options to only those whose treatment variant is available at the store
                    const filteredVariants = item.variantOptions.filter(vo =>
                        vo.treatmentVariant?.storeAssociations?.some(sa => sa.storeId === storeId) ?? false
                    );
                    // Filter add-ons to only those whose treatment variant is available at the store
                    const filteredAddOns = item.addOns.filter(addon =>
                        addon.treatmentVariant?.storeAssociations?.some(sa => sa.storeId === storeId) ?? false
                    );
                    return { ...item, variantOptions: filteredVariants, addOns: filteredAddOns };
                }
                return item;
            })
            .filter(item => {
                // Remove SERVICE items that have no valid variant options for this store
                if (item.type === 'SERVICE') {
                    return item.variantOptions.length > 0;
                }
                // Remove BUNDLE items that have no bundle variant configured for this store
                if (item.type === 'BUNDLE' && item.bundle) {
                    return ('allStoreVariants' in item.bundle)
                        ? item.bundle.allStoreVariants?.some(sv => sv.storeId === storeId) ?? false
                        : true;
                }
                return true;
            });
    }, [bookableItems, storeId]);

    // Keep the bookable item data cache up-to-date with filteredBookableItems
    useEffect(() => {
        for (const item of filteredBookableItems) {
            bookableItemDataCache.current.set(item.id, item);
        }
    }, [filteredBookableItems]);

    // Build autocomplete options
    const bookableItemOptions = useMemo(() => {
        return filteredBookableItems
            .map((item) => {
                const isBundle = item.type === 'BUNDLE';
                
                // For BUNDLE: get duration from bundle.treatmentVariant
                // For SERVICE: get duration from first variantOption
                const treatmentVariantSteps = isBundle 
                    ? item.bundle?.treatmentVariant?.steps
                    : item.variantOptions[0]?.treatmentVariant?.steps;
                
                const totalDuration = treatmentVariantSteps
                    ? Math.max(0, ...treatmentVariantSteps.map(s => s.minuteEnd)) -
                      Math.min(0, ...treatmentVariantSteps.map(s => s.minuteStart))
                    : 0;

                const typeLabel = isBundle ? '🎁 Bundle' : 'Leistung';
                
                // For bundles, show unit info
                const bundleInfo = isBundle && item.bundle 
                    ? ` • ${item.bundle.unitQuantity}x Behandlungen`
                    : '';

                return {
                    label: `${item.nameDe} (${typeLabel})`,
                    value: item.id,
                    renderItem: (
                        <div className='w-full flex flex-col'>
                            <span className='text-xs text-muted-foreground'>
                                {typeLabel}
                                {!isBundle && item.variantOptions.length > 1 && ` • ${item.variantOptions.length} Varianten`}
                                {!isBundle && item.addOns.length > 0 && ` • ${item.addOns.length} Add-Ons`}
                                {bundleInfo}
                            </span>
                            <div className='flex flex-row items-center gap-2'>
                                <span>{item.nameDe}</span>
                                {totalDuration > 0 && (
                                    <span className='text-xs text-muted-foreground'>
                                        ({(() => {
                                            const hours = Math.floor(totalDuration / 60);
                                            const minutes = totalDuration % 60;
                                            if (hours === 0 && minutes === 0) return '0min';
                                            return `${hours > 0 ? `${hours}h` : ''}${minutes > 0 ? `${minutes}min` : ''}`;
                                        })()})
                                    </span>
                                )}
                            </div>
                        </div>
                    ),
                };
            });
    }, [filteredBookableItems]);

    // Add item to store with steps
    const addItemToStore = useCallback((
        item: NonNullable<typeof selectedItem>,
        variantId: string,
        addOns: SelectedAddOn[],
        purchase?: {
            mode: 'ONE_OFF' | 'BUNDLE' | 'REDEMPTION';
            bundle?: { id: string; name: string; unitQuantity: number; unitPriceGross: number };
        }
    ) => {
        const isBundle = item.type === 'BUNDLE';
        
        // For BUNDLE: get variant from bundle.treatmentVariant
        // For SERVICE: get variant from variantOptions
        const variant = isBundle
            ? (item.bundle?.treatmentVariant?.id === variantId ? item.bundle.treatmentVariant : null)
            : item.variantOptions.find(v => v.treatmentVariant?.id === variantId)?.treatmentVariant;
        
        if (!variant || !variant.steps || variant.steps.length === 0) return;

        const itemColor = TREATMENT_COLORS.length > 0
            ? TREATMENT_COLORS[colorIndex % TREATMENT_COLORS.length]
            : '#6366F1';

        // Calculate step start times based on existing steps
        const lastStepEndTime = steps.length > 0 ? Math.max(...steps.map(step => step.minuteEnd)) : 0;

        // Create steps from the main variant
        const mainVariantSteps: Step[] = variant.steps.map((step) => ({
            id: uuidv4(),
            name: step.name,
            shortDescription: step.shortDescription,
            minuteStart: lastStepEndTime + step.minuteStart,
            minuteEnd: lastStepEndTime + step.minuteEnd,
            treatmentColor: itemColor,
            treatmentVariantId: variant.id,
            originalTreatmentStepId: step.id,
            bookableItemId: item.id,
            practitioners: [],
            rooms: [],
            equipment: [],
            initialPractitioners: [],
            initialRooms: [],
            initialEquipment: [],
            selectablePractitioners: [],
            selectableRooms: [],
            selectableEquipment: [],
            acceptablePractitioners: step.practitionerCategoryConstraints?.map((constraint) => ({
                categoryId: constraint.practitionerCategoryId,
            })) ?? [],
            acceptableRooms: step.roomCategoryConstraints?.map((constraint) => ({
                categoryId: constraint.roomCategoryId,
            })) ?? [],
            acceptableEquipments: step.equipmentCategoryConstraints?.map((constraint) => ({
                categoryId: constraint.equipmentCategoryId,
            })) ?? [],
        }));

        // Create steps for each selected add-on, cascading after the main variant steps
        const addOnSteps: Step[] = [];
        let addOnStartOffset = mainVariantSteps.length > 0
            ? Math.max(...mainVariantSteps.map(s => s.minuteEnd))
            : lastStepEndTime;

        for (const selectedAddOn of addOns) {
            // Find the full add-on data (with treatment variant steps) from the item
            const addOnData = item.addOns.find(a => a.id === selectedAddOn.addOnId);
            if (!addOnData?.treatmentVariant?.steps || addOnData.treatmentVariant.steps.length === 0) continue;

            const addOnVariant = addOnData.treatmentVariant;

            for (const step of addOnVariant.steps) {
                addOnSteps.push({
                    id: uuidv4(),
                    name: step.name,
                    shortDescription: step.shortDescription,
                    minuteStart: addOnStartOffset + step.minuteStart,
                    minuteEnd: addOnStartOffset + step.minuteEnd,
                    treatmentColor: itemColor,
                    treatmentVariantId: addOnVariant.id,
                    originalTreatmentStepId: step.id,
                    bookableItemId: item.id,
                    practitioners: [],
                    rooms: [],
                    equipment: [],
                    initialPractitioners: [],
                    initialRooms: [],
                    initialEquipment: [],
                    selectablePractitioners: [],
                    selectableRooms: [],
                    selectableEquipment: [],
                    acceptablePractitioners: step.practitionerCategoryConstraints?.map((constraint) => ({
                        categoryId: constraint.practitionerCategoryId,
                    })) ?? [],
                    acceptableRooms: step.roomCategoryConstraints?.map((constraint) => ({
                        categoryId: constraint.roomCategoryId,
                    })) ?? [],
                    acceptableEquipments: step.equipmentCategoryConstraints?.map((constraint) => ({
                        categoryId: constraint.equipmentCategoryId,
                    })) ?? [],
                });
            }

            // Advance the offset so the next add-on cascades after this one
            addOnStartOffset += Math.max(0, ...addOnVariant.steps.map(s => s.minuteEnd));
        }

        // Combine main variant steps + add-on steps, sorted by start time
        const allNewSteps = [...mainVariantSteps, ...addOnSteps].sort((a, b) => a.minuteStart - b.minuteStart);

        // Resolve bundle metadata + item kind from the chosen purchase mode.
        // Without an explicit mode we keep the historical default: a BUNDLE item
        // is a bundle purchase, a SERVICE item is a one-off service.
        let resolvedType: 'SERVICE' | 'BUNDLE' = isBundle ? 'BUNDLE' : 'SERVICE';
        let resolvedBundle: { id: string; name: string; unitQuantity: number; unitPriceGross: number } | null = null;
        let isRedemption = false;

        if (purchase) {
            if (purchase.mode === 'ONE_OFF') {
                resolvedType = 'SERVICE';
                resolvedBundle = null;
            } else {
                resolvedType = 'BUNDLE';
                resolvedBundle = purchase.bundle
                    ?? (item.bundle
                        ? { id: item.bundle.id, name: item.bundle.name, unitQuantity: item.bundle.unitQuantity, unitPriceGross: item.bundle.unitPriceGross }
                        : null);
                isRedemption = purchase.mode === 'REDEMPTION';
            }
        } else if (isBundle && item.bundle) {
            resolvedBundle = { id: item.bundle.id, name: item.bundle.name, unitQuantity: item.bundle.unitQuantity, unitPriceGross: item.bundle.unitPriceGross };
        }

        // Create the bookable item entry
        const bookableItem: SelectedBookableItem = {
            type: 'bookable_item',
            bookableItemId: item.id,
            bookableItemType: resolvedType,
            bookableItemName: item.nameDe,
            color: itemColor,
            treatmentVariantId: variant.id,
            treatmentVariantName: variant.name,
            priceCents: variant.priceCents,
            ...(resolvedBundle && {
                bundleId: resolvedBundle.id,
                bundleName: resolvedBundle.name,
                bundleUnitQuantity: resolvedBundle.unitQuantity,
                bundleUnitPriceGross: resolvedBundle.unitPriceGross,
            }),
            isBundleRedemption: isRedemption,
            addOns: addOns,
            stepIds: allNewSteps.map(s => s.id),
        };

        // Cache item data for post-selection add-on feature
        bookableItemDataCache.current.set(item.id, item);

        // Add to store
        addBookableItem(bookableItem);
        setSteps([...steps, ...allNewSteps]);
        setColorIndex(colorIndex + 1);

        // Refresh selectable resources
        if (storeId !== undefined) {
            refreshSelectableResources(storeId, timezone, trpcUtils);
        }

        // Reset dialog state
        setDialogOpen(false);
        setSelectedItem(null);
        setSelectedVariantId(null);
        setSelectedAddOns([]);
        setChoiceDialogOpen(false);
        setChoiceItem(null);
        setChoiceVariantId(null);
        setChoiceAddOns([]);
        setChoiceBundle(null);
    }, [steps, colorIndex, storeId, timezone, trpcUtils, addBookableItem, setSteps, setColorIndex, refreshSelectableResources]);

    // Resolve the bundle a given item+variant can be purchased/redeemed with, if any.
    const resolveBundleForSelection = useCallback((
        item: NonNullable<typeof selectedItem>,
        variantId: string
    ): ChoiceBundle | null => {
        if (item.type === 'BUNDLE' && item.bundle) {
            return {
                id: item.bundle.id,
                name: item.bundle.name,
                unitQuantity: item.bundle.unitQuantity,
                unitPriceGross: item.bundle.unitPriceGross,
            };
        }
        if (item.type === 'SERVICE' && 'availableBundles' in item && item.availableBundles) {
            const match = item.availableBundles.find((b) => b.treatmentVariantId === variantId);
            if (match) {
                return {
                    id: match.id,
                    name: match.name,
                    unitQuantity: match.unitQuantity,
                    unitPriceGross: match.unitPriceGross,
                };
            }
        }
        return null;
    }, []);

    // Open the Bundle / One-off / Redemption choice popup for a bundle-linked selection.
    const openChoiceDialog = useCallback((
        item: NonNullable<typeof selectedItem>,
        variantId: string,
        addOns: SelectedAddOn[],
        bundle: ChoiceBundle
    ) => {
        setChoiceItem(item);
        setChoiceVariantId(variantId);
        setChoiceAddOns(addOns);
        setChoiceBundle(bundle);
        setChoiceDialogOpen(true);
    }, []);

    // Handle bookable item selection
    const handleItemSelect = useCallback((value: string) => {
        if (!value) return;

        const item = filteredBookableItems.find((i) => i.id === value);
        if (!item) return;

        const isBundle = item.type === 'BUNDLE';

        // Store the selected item and open dialog
        setSelectedItem(item);
        setSelectedVariantId(null);
        setSelectedAddOns([]);

        if (isBundle) {
            // BUNDLE type: variant is fixed; offer Bundle / One-off / Redemption choice.
            if (item.bundle?.treatmentVariant) {
                const bundle = resolveBundleForSelection(item, item.bundle.treatmentVariant.id);
                if (bundle) {
                    openChoiceDialog(item, item.bundle.treatmentVariant.id, [], bundle);
                } else {
                    addItemToStore(item, item.bundle.treatmentVariant.id, []);
                }
            }
        } else {
            // SERVICE type: check if variant selection is needed
            if (item.variantOptions.length === 1 && item.addOns.length === 0) {
                const variant = item.variantOptions[0];
                if (variant.treatmentVariant) {
                    // If this variant is bundle-linked, offer the choice popup.
                    const bundle = resolveBundleForSelection(item, variant.treatmentVariant.id);
                    if (bundle) {
                        openChoiceDialog(item, variant.treatmentVariant.id, [], bundle);
                    } else {
                        addItemToStore(item, variant.treatmentVariant.id, []);
                    }
                }
            } else {
                // Open dialog for variant/add-on selection
                setDialogOpen(true);
                // Pre-select first variant if available
                if (item.variantOptions.length > 0 && item.variantOptions[0].treatmentVariant) {
                    setSelectedVariantId(item.variantOptions[0].treatmentVariant.id);
                }
            }
        }

        setSearchValue('');
        setSelectedValue('');
    }, [filteredBookableItems, addItemToStore, resolveBundleForSelection, openChoiceDialog]);

    // Handle add-on toggle
    const handleAddOnToggle = useCallback((addOn: NonNullable<typeof selectedItem>['addOns'][0], checked: boolean) => {
        if (!addOn.treatmentVariant) return;

        if (checked) {
            setSelectedAddOns(prev => [...prev, {
                addOnId: addOn.id,
                addOnName: addOn.nameDe ?? addOn.treatmentVariant?.name ?? 'Add-On',
                variantId: addOn.treatmentVariant!.id,
                variantName: addOn.treatmentVariant!.name,
                priceCents: addOn.treatmentVariant!.priceCents,
            }]);
        } else {
            setSelectedAddOns(prev => prev.filter(a => a.addOnId !== addOn.id));
        }
    }, []);

    // Handle dialog confirm
    const handleConfirmSelection = useCallback(() => {
        if (!selectedItem || !selectedVariantId) return;
        // If the chosen variant is bundle-linked, defer to the Bundle / One-off / Redemption popup.
        const bundle = resolveBundleForSelection(selectedItem, selectedVariantId);
        if (bundle) {
            setDialogOpen(false);
            openChoiceDialog(selectedItem, selectedVariantId, selectedAddOns, bundle);
            return;
        }
        addItemToStore(selectedItem, selectedVariantId, selectedAddOns);
    }, [selectedItem, selectedVariantId, selectedAddOns, addItemToStore, resolveBundleForSelection, openChoiceDialog]);

    // Handle remove bookable item
    const handleRemoveItem = useCallback((bookableItemId: string) => {
        removeBookableItem(bookableItemId);
        if (storeId !== undefined) {
            refreshSelectableResources(storeId, timezone, trpcUtils);
        }
    }, [removeBookableItem, storeId, timezone, trpcUtils, refreshSelectableResources]);

    // Handler to add a custom step
    const handleAddCustomStep = useCallback(() => {
        const stepColor = TREATMENT_COLORS.length > 0
            ? TREATMENT_COLORS[colorIndex % TREATMENT_COLORS.length]
            : '#808080';

        const lastStepEndTime = steps.length > 0 ? Math.max(...steps.map(step => step.minuteEnd)) : 0;
        const customStepStart = lastStepEndTime;
        const customStepEnd = customStepStart + 15;

        const newCustomStep: Step = {
            id: uuidv4(),
            name: "Neuer Schritt",
            shortDescription: "",
            minuteStart: customStepStart,
            minuteEnd: customStepEnd,
            treatmentColor: stepColor,
            treatmentVariantId: null,
            originalTreatmentStepId: null,
            bookableItemId: null,
            selectablePractitioners: [],
            selectableRooms: [],
            selectableEquipment: [],
            practitioners: [],
            rooms: [],
            equipment: [],
            initialPractitioners: [],
            initialRooms: [],
            initialEquipment: [],
            acceptablePractitioners: [],
            acceptableRooms: [],
            acceptableEquipments: [],
        };

        setSteps([...steps, newCustomStep]);
        setColorIndex(colorIndex + 1);

        if (storeId !== undefined) {
            refreshSelectableResources(storeId, timezone, trpcUtils);
        }
    }, [steps, setSteps, colorIndex, setColorIndex, storeId, timezone, trpcUtils, refreshSelectableResources]);

    // Handle confirming post-selection add-on changes (additions AND removals)
    const handleConfirmPostAddOns = useCallback(() => {
        if (!addOnPopoverOpenFor) {
            setAddOnPopoverOpenFor(null);
            setPendingAddOnSelections([]);
            return;
        }

        const targetItem = selectedBookableItems.find(i => i.bookableItemId === addOnPopoverOpenFor);
        if (!targetItem) return;

        const itemData = bookableItemDataCache.current.get(addOnPopoverOpenFor);
        if (!itemData) return;

        // Filter add-ons by store availability
        const storeFilteredAddOns = storeId
            ? itemData.addOns.filter(addon =>
                addon.treatmentVariant?.storeAssociations?.some(sa => sa.storeId === storeId) ?? false
            )
            : itemData.addOns;

        // Compute diff: which add-ons to add and which to remove
        const existingAddOnIds = targetItem.addOns.map(a => a.addOnId);
        const addOnIdsToAdd = pendingAddOnSelections.filter(id => !existingAddOnIds.includes(id));
        const addOnIdsToRemove = existingAddOnIds.filter(id => !pendingAddOnSelections.includes(id));

        // If nothing changed, just close
        if (addOnIdsToAdd.length === 0 && addOnIdsToRemove.length === 0) {
            setAddOnPopoverOpenFor(null);
            setPendingAddOnSelections([]);
            return;
        }

        let currentSteps = [...steps];
        let currentStepIds = [...targetItem.stepIds];
        let currentAddOns = [...targetItem.addOns];

        // ── REMOVE add-ons ──
        // Process removals sequentially so timing shifts accumulate correctly
        for (const removeId of addOnIdsToRemove) {
            const removedAddOn = currentAddOns.find(a => a.addOnId === removeId);
            if (!removedAddOn) continue;

            // Find steps belonging to this add-on (within the bookable item's step IDs)
            const addOnStepIds = currentSteps
                .filter(s =>
                    currentStepIds.includes(s.id) &&
                    s.treatmentVariantId === removedAddOn.variantId
                )
                .map(s => s.id);

            if (addOnStepIds.length > 0) {
                // Calculate the gap left by the removed steps
                const addOnSteps = currentSteps.filter(s => addOnStepIds.includes(s.id));
                const gapStart = Math.min(...addOnSteps.map(s => s.minuteStart));
                const gapEnd = Math.max(...addOnSteps.map(s => s.minuteEnd));
                const gapDuration = gapEnd - gapStart;

                // Remove the add-on's steps
                currentSteps = currentSteps.filter(s => !addOnStepIds.includes(s.id));

                // Shift all steps that start at or after gapEnd backward by gapDuration
                currentSteps = currentSteps.map(s => {
                    if (s.minuteStart >= gapEnd) {
                        return {
                            ...s,
                            minuteStart: s.minuteStart - gapDuration,
                            minuteEnd: s.minuteEnd - gapDuration,
                        };
                    }
                    return s;
                });

                // Remove step IDs from the bookable item's tracking
                currentStepIds = currentStepIds.filter(id => !addOnStepIds.includes(id));
            }

            // Remove from the add-on list
            currentAddOns = currentAddOns.filter(a => a.addOnId !== removeId);
        }

        // ── ADD new add-ons ──
        const newAddOnSteps: Step[] = [];
        const newSelectedAddOns: SelectedAddOn[] = [];

        if (addOnIdsToAdd.length > 0) {
            // Find the insertion point: end time of the last step belonging to this bookable item
            const itemSteps = currentSteps.filter(s => currentStepIds.includes(s.id));
            const insertionPoint = itemSteps.length > 0
                ? Math.max(...itemSteps.map(s => s.minuteEnd))
                : 0;

            let addOnStartOffset = insertionPoint;

            for (const addOnId of addOnIdsToAdd) {
                const addOnData = storeFilteredAddOns.find(a => a.id === addOnId);
                if (!addOnData?.treatmentVariant?.steps || addOnData.treatmentVariant.steps.length === 0) continue;

                const addOnVariant = addOnData.treatmentVariant;

                newSelectedAddOns.push({
                    addOnId: addOnData.id,
                    addOnName: addOnData.nameDe ?? addOnVariant.name ?? 'Add-On',
                    variantId: addOnVariant.id,
                    variantName: addOnVariant.name,
                    priceCents: addOnVariant.priceCents,
                });

                for (const step of addOnVariant.steps) {
                    newAddOnSteps.push({
                        id: uuidv4(),
                        name: step.name,
                        shortDescription: step.shortDescription,
                        minuteStart: addOnStartOffset + step.minuteStart,
                        minuteEnd: addOnStartOffset + step.minuteEnd,
                        treatmentColor: targetItem.color,
                        treatmentVariantId: addOnVariant.id,
                        originalTreatmentStepId: step.id,
                        bookableItemId: targetItem.bookableItemId,
                        practitioners: [],
                        rooms: [],
                        equipment: [],
                        initialPractitioners: [],
                        initialRooms: [],
                        initialEquipment: [],
                        selectablePractitioners: [],
                        selectableRooms: [],
                        selectableEquipment: [],
                        acceptablePractitioners: step.practitionerCategoryConstraints?.map((constraint) => ({
                            categoryId: constraint.practitionerCategoryId,
                        })) ?? [],
                        acceptableRooms: step.roomCategoryConstraints?.map((constraint) => ({
                            categoryId: constraint.roomCategoryId,
                        })) ?? [],
                        acceptableEquipments: step.equipmentCategoryConstraints?.map((constraint) => ({
                            categoryId: constraint.equipmentCategoryId,
                        })) ?? [],
                    });
                }

                // Advance offset so the next add-on cascades after this one
                addOnStartOffset += Math.max(0, ...addOnVariant.steps.map(s => s.minuteEnd));
            }

            if (newAddOnSteps.length > 0) {
                // Calculate total new duration to shift subsequent steps
                const totalNewDuration = addOnStartOffset - insertionPoint;

                // Shift all existing steps that start at or after the insertion point
                // and do NOT belong to this bookable item
                currentSteps = currentSteps.map(step => {
                    if (step.minuteStart >= insertionPoint && !currentStepIds.includes(step.id)) {
                        return {
                            ...step,
                            minuteStart: step.minuteStart + totalNewDuration,
                            minuteEnd: step.minuteEnd + totalNewDuration,
                        };
                    }
                    return step;
                });
            }
        }

        // Merge steps and sort by start time
        const allSteps = [...currentSteps, ...newAddOnSteps].sort((a, b) => a.minuteStart - b.minuteStart);
        setSteps(allSteps);

        // Update the target bookable item's addOns and stepIds
        const finalAddOns = [...currentAddOns, ...newSelectedAddOns];
        const finalStepIds = [...currentStepIds, ...newAddOnSteps.map(s => s.id)];

        const updatedBookableItems = selectedBookableItems.map(item => {
            if (item.bookableItemId === addOnPopoverOpenFor) {
                return {
                    ...item,
                    addOns: finalAddOns,
                    stepIds: finalStepIds,
                };
            }
            return item;
        });
        setSelectedBookableItems(updatedBookableItems);

        // Refresh selectable resources
        if (storeId !== undefined) {
            refreshSelectableResources(storeId, timezone, trpcUtils);
        }

        // Close popover and reset
        setAddOnPopoverOpenFor(null);
        setPendingAddOnSelections([]);
    }, [addOnPopoverOpenFor, pendingAddOnSelections, selectedBookableItems, steps, storeId, timezone, trpcUtils, setSteps, setSelectedBookableItems, refreshSelectableResources]);

    return (
        <div className='space-y-2'>
            <div className='flex justify-between items-center'>
                <Label>Leistung / Bundle</Label>
            </div>
            {!storeId ? (
                <p className='text-sm text-muted-foreground italic'>
                    Bitte zuerst einen Standort auswählen.
                </p>
            ) : (
                <AutoComplete
                    selectedValue={selectedValue}
                    onSelectedValueChange={(value) => {
                        setSelectedValue(value);
                        handleItemSelect(value);
                    }}
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    items={bookableItemOptions}
                    placeholder='Leistung oder Bundle auswählen...'
                    emptyMessage='Keine Leistung gefunden'
                    isLoading={bookableItemsQuery.isLoading}
                    resetSearchOnSelectItem
                />
            )}

            {/* Add Custom Step Button */}
            <Button
                type="button"
                variant="outline"
                onClick={handleAddCustomStep}
                className="w-full mt-2 sm:w-auto"
            >
                + Schritt hinzufügen
            </Button>

            {/* Display Selected Bookable Items */}
            <div className='flex flex-wrap gap-2 mt-4'>
                {selectedBookableItems.map((item) => {
                    const cachedItemData = bookableItemDataCache.current.get(item.bookableItemId);

                    // Get all available add-ons for this bookable item (filtered by store)
                    const allStoreAddOns = cachedItemData && item.type === 'bookable_item' && item.bookableItemType === 'SERVICE'
                        ? (storeId
                            ? cachedItemData.addOns.filter(addon =>
                                addon.treatmentVariant?.storeAssociations?.some(sa => sa.storeId === storeId) ?? false
                            )
                            : cachedItemData.addOns
                        )
                        : [];

                    const canManageAddOns = allStoreAddOns.length > 0;

                    return (
                        <div key={item.bookableItemId} className='flex flex-col items-start gap-1'>
                            {/* Badge */}
                            <div
                                className='inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border'
                                style={{
                                    backgroundColor: `${item.color || '#6366F1'}20`,
                                    color: item.color || '#6366F1',
                                    borderColor: item.color || '#6366F1'
                                }}
                            >
                                <span>
                                    {item.bookableItemName}
                                    {item.addOns.length > 0 && (
                                        <span className='text-muted-foreground ml-1'>
                                            (+{item.addOns.length})
                                        </span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="ml-1.5 shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full text-current hover:bg-current/20 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
                                    onClick={() => handleRemoveItem(item.bookableItemId)}
                                >
                                    <span className="sr-only">Entfernen {item.bookableItemName}</span>
                                    <svg className="h-2.5 w-2.5" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Add-On button with popover (only for SERVICE bookable items with available add-ons) */}
                            {canManageAddOns && (
                                <Popover
                                    open={addOnPopoverOpenFor === item.bookableItemId}
                                    onOpenChange={(open) => {
                                        if (open) {
                                            setAddOnPopoverOpenFor(item.bookableItemId);
                                            // Pre-populate with currently selected add-on IDs
                                            setPendingAddOnSelections(item.addOns.map(a => a.addOnId));
                                        } else {
                                            setAddOnPopoverOpenFor(null);
                                            setPendingAddOnSelections([]);
                                        }
                                    }}
                                >
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2 cursor-pointer px-1"
                                        >
                                            Add-Ons
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 p-3" align="start">
                                        {allStoreAddOns.length === 0 ? (
                                            <p className="text-xs text-muted-foreground">
                                                Keine Add-Ons verfügbar.
                                            </p>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Add-Ons auswählen</p>
                                                <div className="grid gap-1.5 max-h-48 overflow-y-auto">
                                                    {allStoreAddOns.map((addOn) => (
                                                        addOn.treatmentVariant && (
                                                            <label
                                                                key={addOn.id}
                                                                className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer"
                                                            >
                                                                <Checkbox
                                                                    checked={pendingAddOnSelections.includes(addOn.id)}
                                                                    onCheckedChange={(checked) => {
                                                                        setPendingAddOnSelections(prev =>
                                                                            checked
                                                                                ? [...prev, addOn.id]
                                                                                : prev.filter(id => id !== addOn.id)
                                                                        );
                                                                    }}
                                                                />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-xs font-medium truncate">
                                                                        {addOn.nameDe || addOn.treatmentVariant.name}
                                                                    </div>
                                                                    <div className="text-[10px] text-muted-foreground">
                                                                        +{(addOn.treatmentVariant.priceCents / 100).toLocaleString('de-DE', {
                                                                            style: 'currency',
                                                                            currency: 'EUR',
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        )
                                                    ))}
                                                </div>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    className="w-full mt-1"
                                                    disabled={
                                                        // Disable when selection hasn't changed from current state
                                                        pendingAddOnSelections.length === item.addOns.length &&
                                                        pendingAddOnSelections.every(id => item.addOns.some(a => a.addOnId === id))
                                                    }
                                                    onClick={handleConfirmPostAddOns}
                                                >
                                                    Übernehmen
                                                </Button>
                                            </div>
                                        )}
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bundle / One-off / Redemption choice dialog */}
            <Dialog open={choiceDialogOpen} onOpenChange={setChoiceDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {choiceItem?.nameDe} – Option wählen
                        </DialogTitle>
                    </DialogHeader>

                    {choiceItem && choiceVariantId && choiceBundle && (() => {
                        const oneOffPriceCents = choiceItem.type === 'BUNDLE'
                            ? (choiceItem.bundle?.treatmentVariant?.priceCents ?? 0)
                            : (choiceItem.variantOptions.find(vo => vo.treatmentVariant?.id === choiceVariantId)?.treatmentVariant?.priceCents ?? 0);
                        const fmt = (value: number) => value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
                        const bundleTotal = choiceBundle.unitPriceGross * choiceBundle.unitQuantity;

                        return (
                            <div className="grid gap-2">
                                <button
                                    type="button"
                                    className="p-3 rounded-lg border border-gray-200 text-left transition-colors hover:border-gray-300"
                                    onClick={() => addItemToStore(choiceItem, choiceVariantId, choiceAddOns, { mode: 'ONE_OFF' })}
                                >
                                    <div className="font-medium">Einzeltermin</div>
                                    <div className="text-sm text-muted-foreground">{fmt(oneOffPriceCents / 100)}</div>
                                </button>
                                <button
                                    type="button"
                                    className="p-3 rounded-lg border border-gray-200 text-left transition-colors hover:border-gray-300"
                                    onClick={() => addItemToStore(choiceItem, choiceVariantId, choiceAddOns, { mode: 'BUNDLE', bundle: choiceBundle })}
                                >
                                    <div className="font-medium">Bundle kaufen: {choiceBundle.name}</div>
                                    <div className="text-sm text-muted-foreground">
                                        {choiceBundle.unitQuantity} × {fmt(choiceBundle.unitPriceGross)} = {fmt(bundleTotal)}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className="p-3 rounded-lg border border-gray-200 text-left transition-colors hover:border-gray-300"
                                    onClick={() => addItemToStore(choiceItem, choiceVariantId, choiceAddOns, { mode: 'REDEMPTION', bundle: choiceBundle })}
                                >
                                    <div className="font-medium">Bundle einlösen</div>
                                    <div className="text-sm text-muted-foreground">
                                        {fmt(choiceBundle.unitPriceGross)} (bestehendes Bundle)
                                    </div>
                                </button>
                            </div>
                        );
                    })()}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setChoiceDialogOpen(false)}>
                            Abbrechen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Variant/Add-on Selection Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedItem?.nameDe} konfigurieren
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Variant Selection — filtered by store availability */}
                        {selectedItem && (() => {
                            const storeFilteredVariants = storeId
                                ? selectedItem.variantOptions.filter(opt =>
                                    opt.treatmentVariant?.storeAssociations?.some(sa => sa.storeId === storeId) ?? false
                                )
                                : selectedItem.variantOptions;

                            if (storeFilteredVariants.length <= 1) return null;

                            return (
                                <div className="space-y-2">
                                    <Label>Variante auswählen</Label>
                                    <div className="grid gap-2">
                                        {storeFilteredVariants.map((opt) => (
                                            opt.treatmentVariant && (
                                                <button
                                                    key={opt.treatmentVariant.id}
                                                    type="button"
                                                    className={`p-3 rounded-lg border text-left transition-colors ${
                                                        selectedVariantId === opt.treatmentVariant.id
                                                            ? 'border-primary bg-primary/5'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    onClick={() => setSelectedVariantId(opt.treatmentVariant!.id)}
                                                >
                                                    <div className="font-medium">{opt.treatmentVariant.name}</div>
                                                    <div className="text-sm text-muted-foreground">
                                                        {(opt.treatmentVariant.priceCents / 100).toLocaleString('de-DE', {
                                                            style: 'currency',
                                                            currency: 'EUR',
                                                        })}
                                                        {opt.treatmentVariant.steps && opt.treatmentVariant.steps.length > 0 && (
                                                            <>
                                                                {' • '}
                                                                {(() => {
                                                                    const totalMins = Math.max(0, ...opt.treatmentVariant.steps.map(s => s.minuteEnd));
                                                                    const h = Math.floor(totalMins / 60);
                                                                    const m = totalMins % 60;
                                                                    return `${h > 0 ? `${h}h` : ''}${m > 0 ? `${m}min` : ''}`;
                                                                })()}
                                                            </>
                                                        )}
                                                    </div>
                                                </button>
                                            )
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Add-ons Selection (only for SERVICE type) — filtered by store availability */}
                        {selectedItem && selectedItem.type === 'SERVICE' && (() => {
                            const storeFilteredAddOns = storeId
                                ? selectedItem.addOns.filter(addOn =>
                                    addOn.treatmentVariant?.storeAssociations?.some(sa => sa.storeId === storeId) ?? false
                                )
                                : selectedItem.addOns;

                            if (storeFilteredAddOns.length === 0) return null;

                            return (
                                <div className="space-y-2">
                                    <Label>Add-Ons (optional)</Label>
                                    <div className="grid gap-2">
                                        {storeFilteredAddOns.map((addOn) => (
                                            addOn.treatmentVariant && (
                                                <label
                                                    key={addOn.id}
                                                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer"
                                                >
                                                    <Checkbox
                                                        checked={selectedAddOns.some(a => a.addOnId === addOn.id)}
                                                        onCheckedChange={(checked) => handleAddOnToggle(addOn, !!checked)}
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium">{addOn.nameDe || addOn.treatmentVariant.name}</div>
                                                        <div className="text-sm text-muted-foreground">
                                                            +{(addOn.treatmentVariant.priceCents / 100).toLocaleString('de-DE', {
                                                                style: 'currency',
                                                                currency: 'EUR',
                                                            })}
                                                        </div>
                                                    </div>
                                                </label>
                                            )
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleConfirmSelection}
                            disabled={!selectedVariantId}
                        >
                            Hinzufügen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
